import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SummaryPreview = ({ summaryData, dateRange }) => {
  const [expandedEntry, setExpandedEntry] = useState(null);

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date)?.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    if (severity >= 8) return 'text-red-600';
    if (severity >= 5) return 'text-amber-600';
    return 'text-green-600';
  };

  const getSeverityBadgeStyle = (severity) => {
    if (severity >= 8) return 'bg-red-50 text-red-700 border border-red-200';
    if (severity >= 5) return 'bg-amber-50 text-amber-700 border border-amber-200';
    return 'bg-green-50 text-green-700 border border-green-200';
  };

  const getSeverityLabel = (severity) => {
    if (severity >= 8) return 'Severe';
    if (severity >= 5) return 'Moderate';
    if (severity >= 3) return 'Mild';
    return 'Minimal';
  };

  const getTrendIcon = (direction) => {
    if (direction === 'increasing') return '↑';
    if (direction === 'decreasing') return '↓';
    return '→';
  };

  const getTrendStyle = (direction) => {
    if (direction === 'increasing') return 'text-red-600 bg-red-50 border border-red-200';
    if (direction === 'decreasing') return 'text-green-600 bg-green-50 border border-green-200';
    return 'text-amber-600 bg-amber-50 border border-amber-200';
  };

  const formatLabel = (str) => {
    return str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
  };

  const rawEntries = summaryData?.rawEntries || [];
  const hasEntries = rawEntries?.length > 0;

  // Determine primary concern (highest combined severity + frequency score)
  const primaryConcern = summaryData?.symptoms?.length > 0
    ? [...summaryData?.symptoms]?.sort((a, b) => {
        const scoreA = (a?.avgSeverity || 0) * 0.6 + (a?.frequency || 0) * 0.4;
        const scoreB = (b?.avgSeverity || 0) * 0.6 + (b?.frequency || 0) * 0.4;
        return scoreB - scoreA;
      })?.[0]
    : null;

  const primaryTrend = primaryConcern
    ? summaryData?.trends?.find(t => t?.symptom === primaryConcern?.name)
    : null;

  const primaryImpactLevel = primaryConcern
    ? primaryConcern?.avgSeverity >= 8 ? 'High'
      : primaryConcern?.avgSeverity >= 5 ? 'Moderate' :'Low'
    : null;

  // Notes
  const allNotes = rawEntries?.filter(e => e?.notes);
  const notesSummary = allNotes?.slice(0, 3)?.map(e => e?.notes)?.join(' ');

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" id="summary-preview-print">
      {/* ===== REPORT HEADER ===== */}
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 rounded-t-xl px-6 py-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png"
              alt="MyBodyToday Logo"
              className="w-9 h-9 object-contain bg-white/10 rounded-lg p-1"
            />
            <div>
              <h2 className="text-xl font-bold tracking-tight">Patient Health Summary Report</h2>
              <p className="text-indigo-200 text-sm mt-0.5">MyBodyToday — Symptom Tracking Report</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="text-indigo-200">Generated</p>
            <p className="font-medium">{formatDateTime(new Date())}</p>
          </div>
        </div>
      </div>
      <div className="p-6 md:p-8 space-y-8">

        {/* ===== SECTION 1: REPORT OVERVIEW ===== */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">1</span>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Report Overview</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Report Period</p>
              <p className="text-sm font-semibold text-gray-900">{formatDate(dateRange?.start)}</p>
              <p className="text-xs text-gray-500">to {formatDate(dateRange?.end)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Total Entries</p>
              <p className="text-3xl font-bold text-indigo-700">{rawEntries?.length}</p>
              <p className="text-xs text-gray-500">symptom check-ins</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Symptoms Tracked</p>
              <p className="text-3xl font-bold text-indigo-700">{summaryData?.symptoms?.length || 0}</p>
              <p className="text-xs text-gray-500">distinct symptom types</p>
            </div>
          </div>
        </section>

        {!hasEntries ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl">
            <Icon name="FileText" size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No check-in entries found for this period.</p>
            <p className="text-gray-400 text-sm mt-1">Adjust the date range to view symptom data.</p>
          </div>
        ) : (
          <>
            {/* ===== SECTION 2: PRIMARY CONCERN ===== */}
            {primaryConcern && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex-shrink-0">2</span>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Primary Concern</h3>
                  <span className="text-xs text-gray-400 font-normal">(auto-identified by severity &amp; frequency)</span>
                </div>
                <div className="border-2 border-red-200 bg-red-50 rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{formatLabel(primaryConcern?.name)}</h4>
                      <p className="text-sm text-gray-600 mt-0.5">
                        <span className="font-medium">Location:</span> {formatLabel(primaryConcern?.location)}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getSeverityBadgeStyle(primaryConcern?.avgSeverity)}`}>
                      {getSeverityLabel(primaryConcern?.avgSeverity)} Impact
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div className="bg-white rounded-lg p-3 border border-red-100 text-center">
                      <p className="text-xs text-gray-500 mb-1">Avg Severity</p>
                      <p className={`text-xl font-bold ${getSeverityColor(primaryConcern?.avgSeverity)}`}>
                        {primaryConcern?.avgSeverity}/10
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-100 text-center">
                      <p className="text-xs text-gray-500 mb-1">Trend</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-semibold ${getTrendStyle(primaryTrend?.direction || 'stable')}`}>
                        {getTrendIcon(primaryTrend?.direction || 'stable')} {formatLabel(primaryTrend?.direction || 'Stable')}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-100 text-center">
                      <p className="text-xs text-gray-500 mb-1">Frequency</p>
                      <p className="text-xl font-bold text-gray-900">{primaryConcern?.frequency}×</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-100 text-center">
                      <p className="text-xs text-gray-500 mb-1">Impact Level</p>
                      <p className="text-sm font-bold text-gray-900">{primaryImpactLevel}</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ===== SECTION 3: SYMPTOM SUMMARY TABLE ===== */}
            {summaryData?.symptoms?.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">3</span>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Symptom Summary</h3>
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Symptom</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Location</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Avg Severity</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Frequency</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summaryData?.symptoms?.map((symptom, index) => {
                        const trend = summaryData?.trends?.find(t => t?.symptom === symptom?.name);
                        return (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-4 py-3 font-medium text-gray-900">{formatLabel(symptom?.name)}</td>
                            <td className="px-4 py-3 text-gray-600">{formatLabel(symptom?.location)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getSeverityBadgeStyle(symptom?.avgSeverity)}`}>
                                {symptom?.avgSeverity}/10
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-700 font-medium">{symptom?.frequency}×</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-semibold ${getTrendStyle(trend?.direction || 'stable')}`}>
                                {getTrendIcon(trend?.direction || 'stable')} {formatLabel(trend?.direction || 'Stable')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ===== SECTION 4: SYMPTOM TIMELINE ===== */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">4</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Symptom Timeline</h3>
              </div>
              <div className="space-y-2">
                {rawEntries?.slice()?.sort((a, b) => new Date(a?.date) - new Date(b?.date))?.map((entry, index) => (
                  <div key={entry?.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex-shrink-0 text-center min-w-[72px]">
                      <p className="text-xs font-semibold text-gray-700">
                        {new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(entry?.date)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {entry?.symptomCategories?.slice(0, 3)?.map((cat, i) => (
                          <span key={i} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded font-medium">
                            {formatLabel(cat)}
                          </span>
                        ))}
                        {entry?.symptomCategories?.length > 3 && (
                          <span className="text-xs text-gray-400">+{entry?.symptomCategories?.length - 3} more</span>
                        )}
                      </div>
                      {entry?.notes && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{entry?.notes}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {entry?.severityNumeric > 0 && (
                        <span className={`text-xs font-bold px-2 py-1 rounded ${getSeverityBadgeStyle(entry?.severityNumeric)}`}>
                          {entry?.severityNumeric}/10
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ===== SECTION 5: TRIGGER OBSERVATIONS ===== */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">5</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Trigger Observations</h3>
              </div>
              {summaryData?.triggers?.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-800 mb-3">The following triggers or contexts were reported by the patient:</p>
                  <div className="flex flex-wrap gap-2">
                    {summaryData?.triggers?.map((trigger, index) => (
                      <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-amber-800 border border-amber-300 rounded-lg text-sm font-medium shadow-sm">
                        <Icon name="AlertCircle" size={13} className="text-amber-500" />
                        {trigger}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500 italic">No specific triggers reported during this period.</p>
                </div>
              )}
            </section>

            {/* ===== SECTION 6: IMPACT ON DAILY LIFE ===== */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">6</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Impact on Daily Life</h3>
              </div>
              {summaryData?.impacts?.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { fallback: 'mobil', label: 'Mobility', icon: 'Activity' },
                      { fallback: 'sleep', label: 'Sleep', icon: 'Moon' },
                      { fallback: 'social', label: 'Social Activities', icon: 'Users' },
                      { fallback: 'work', label: 'Work / Concentration', icon: 'Briefcase' }
                    ]?.map(({ fallback, label, icon }) => {
                      const impact = summaryData?.impacts?.find(i =>
                        i?.category?.toLowerCase()?.includes(fallback) ||
                        (fallback === 'work' && i?.category?.toLowerCase()?.includes('concentrat'))
                      );
                      return (
                        <div key={fallback} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                          <Icon name={icon} size={18} className="text-indigo-500 mx-auto mb-2" />
                          <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
                          {impact ? (
                            <>
                              <p className={`text-2xl font-bold ${getSeverityColor(impact?.score)}`}>{impact?.score}/10</p>
                              <p className="text-xs text-gray-400 mt-0.5">{getSeverityLabel(impact?.score)}</p>
                            </>
                          ) : (
                            <p className="text-sm text-gray-400 italic mt-1">Not reported</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Other impacts */}
                  {summaryData?.impacts?.filter(i =>
                    !['mobil', 'sleep', 'social', 'work', 'concentrat']?.some(k => i?.category?.toLowerCase()?.includes(k))
                  )?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {summaryData?.impacts?.filter(i =>
                        !['mobil', 'sleep', 'social', 'work', 'concentrat']?.some(k => i?.category?.toLowerCase()?.includes(k))
                      )?.map((impact, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg">
                          {formatLabel(impact?.category)}: <strong>{impact?.score}/10</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500 italic">No daily impact data recorded for this period.</p>
                </div>
              )}
            </section>

            {/* ===== SECTION 7: MEDICATION SUMMARY ===== */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">7</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Medication Summary</h3>
              </div>
              {summaryData?.medications?.length > 0 || rawEntries?.some(e => e?.medicationDetails) ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  {summaryData?.medications?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Medication Status Reported</p>
                      <div className="flex flex-wrap gap-2">
                        {summaryData?.medications?.map((med, index) => (
                          <span key={index} className="text-sm bg-white text-blue-800 border border-blue-200 px-3 py-1 rounded-lg font-medium">
                            {med?.status} — {med?.count} day{med?.count !== 1 ? 's' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {rawEntries?.some(e => e?.medicationDetails) && (
                    <div>
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Medication Details</p>
                      <div className="space-y-2">
                        {rawEntries?.filter(e => e?.medicationDetails)?.map((entry, i) => (
                          <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                            <p className="text-xs text-blue-500 mb-1">{formatDateTime(entry?.date)}</p>
                            <p className="text-sm text-gray-800">{entry?.medicationDetails}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <Icon name="Pill" size={16} className="text-gray-400" />
                    No medications recorded during this period.
                  </p>
                </div>
              )}
            </section>

            {/* ===== SECTION 8: PATIENT NOTES SUMMARY ===== */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">8</span>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Patient Notes Summary</h3>
              </div>
              {notesSummary ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Key Concerns (from patient notes)</p>
                  <p className="text-sm text-amber-900 leading-relaxed">
                    {notesSummary?.slice(0, 400)}{notesSummary?.length > 400 ? '...' : ''}
                  </p>
                  {allNotes?.length > 3 && (
                    <p className="text-xs text-amber-600 mt-2">+ {allNotes?.length - 3} additional note entries — see Section 9 for full details.</p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500 italic">No patient notes recorded during this period.</p>
                </div>
              )}
            </section>

            {/* ===== SECTION 9: DETAILED NOTES ===== */}
            {allNotes?.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">9</span>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Detailed Notes</h3>
                </div>
                <div className="space-y-3">
                  {allNotes?.map((entry, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                        onClick={() => setExpandedEntry(expandedEntry === `note-${index}` ? null : `note-${index}`)}
                      >
                        <span className="text-sm font-medium text-gray-700">{formatDateTime(entry?.date)}</span>
                        <Icon name={expandedEntry === `note-${index}` ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-gray-400" />
                      </button>
                      <div className="px-4 py-3 bg-white">
                        {expandedEntry === `note-${index}` ? (
                          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{entry?.notes}</p>
                        ) : (
                          <p className="text-sm text-gray-600 truncate">{entry?.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ===== PHOTO GALLERY ===== */}
            {rawEntries?.some(e => e?.photos?.length > 0) && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Camera" size={18} className="text-indigo-600" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Symptom Photos</h3>
                </div>
                <div className="space-y-4">
                  {rawEntries?.filter(e => e?.photos?.length > 0)?.map((entry, ei) => (
                    <div key={ei}>
                      <p className="text-xs text-gray-500 font-medium mb-2">{formatDateTime(entry?.date)}</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {entry?.photos?.map((photo, pi) => (
                          <div key={pi} className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                            <img
                              src={photo?.preview || photo?.url || photo}
                              alt={photo?.name || `Symptom photo ${pi + 1}`}
                              className="w-full h-32 object-cover"
                            />
                            {photo?.name && (
                              <div className="bg-black/60 text-white text-xs p-1.5 truncate">{photo?.name}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* ===== SECTION 10: SAFETY DISCLAIMER ===== */}
        <section className="border-t border-gray-200 pt-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex-shrink-0">10</span>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Safety Disclaimer</h3>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="ShieldAlert" size={18} className="text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-gray-800">Important:</strong> This report is a patient-generated symptom summary and is not a medical diagnosis.
                All data is self-reported by the patient using the MyBodyToday application. This document is intended to support — not replace —
                a clinical consultation with a qualified healthcare professional. Please consult a qualified healthcare professional for diagnosis,
                treatment, or medical advice.
              </p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default SummaryPreview;